import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgentNotifications } from "../../../context/AgentNotificationContext";
import usePixieDustNavigation from "../../../hooks/usePixieDustNavigation";
import { 
  Bell, 
  BellOff,
  CheckCircle, 
  Clock, 
  Filter,
  FileText,
  Users,
  Calendar,
  DollarSign,
  AlertCircle,
  Trash2,
  Check,
  Settings,
  RefreshCw
} from "lucide-react";
import toast from "../../../utils/toast";
import "../../../styles/modern-design-system.css";
import PageHeader from "../../../components/ui/PageHeader";

export default function ModernNotifications() {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    isConnected,
    fetchNotifications,
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useAgentNotifications();
  const { navigateToUnifiedItinerary } = usePixieDustNavigation();

  const [filter, setFilter] = useState("all"); // all, unread, read
  const [typeFilter, setTypeFilter] = useState("all"); // all, new_lead, new_trip, etc.
  const [showFilters, setShowFilters] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const notificationTypes = [
    { key: "all", label: "All Types", icon: Bell },
    { key: "new_lead", label: "New Leads", icon: Users },
    { key: "new_trip", label: "New Trips", icon: Calendar },
    { key: "questionnaire_submitted", label: "Questionnaires", icon: FileText },
    { key: "lead_converted", label: "Conversions", icon: CheckCircle },
    { key: "new_commission", label: "Commissions", icon: DollarSign },
    { key: "system", label: "System", icon: AlertCircle },
  ];

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    // Filter by read status
    if (filter === "unread" && notification.is_read) return false;
    if (filter === "read" && !notification.is_read) return false;

    // Filter by type
    if (typeFilter !== "all" && notification.type !== typeFilter) return false;

    return true;
  });

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notificationid);
    }

    // Handle navigation based on notification type
    if (notification.data?.action) {
      switch (notification.data.action) {
        case 'view_itinerary':
          navigateToUnifiedItinerary(notification.data.tripId);
          break;
        case 'view_trip':
          navigate(`/trips`);
          break;
        case 'view_client':
          navigate(`/clients`);
          break;
        case 'view_lead':
          navigate(`/leads`);
          break;
        case 'view_commission':
          navigate(`/commissions`);
          break;
        default:
          break;
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    // Show confirmation on first click, execute on second
    if (confirmDeleteId !== notificationId) {
      setConfirmDeleteId(notificationId);
      // Auto-clear after 3 seconds if not confirmed
      setTimeout(() => setConfirmDeleteId((prev) => prev === notificationId ? null : prev), 3000);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deleteNotification(notificationId);
      toast.success('Notification deleted');
    } catch {
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'questionnaire_submitted':
        return <FileText className="w-5 h-5 text-gray-600" />;
      case 'new_trip':
        return <Calendar className="w-5 h-5 text-gray-600" />;
      case 'new_lead':
        return <Users className="w-5 h-5 text-gray-600" />;
      case 'lead_converted':
        return <CheckCircle className="w-5 h-5 text-gray-600" />;
      case 'new_commission':
        return <DollarSign className="w-5 h-5 text-gray-600" />;
      case 'payment_processed':
        return <DollarSign className="w-5 h-5 text-gray-600" />;
      case 'new_client':
        return <Users className="w-5 h-5 text-gray-600" />;
      case 'trip_status_changed':
        return <Calendar className="w-5 h-5 text-gray-600" />;
      case 'reminder':
        return <Clock className="w-5 h-5 text-gray-600" />;
      case 'system':
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown time';
      
      const now = new Date();
      let notificationTime;
      
      if (typeof timestamp === 'string') {
        if (timestamp.includes(' ')) {
          const isoString = timestamp.replace(' ', 'T') + 'Z';
          notificationTime = new Date(isoString);
        } else {
          notificationTime = new Date(timestamp);
        }
      } else {
        notificationTime = new Date(timestamp);
      }
      
      if (isNaN(notificationTime.getTime())) {
        return 'Unknown time';
      }
      
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
      return notificationTime.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting time ago:', error);
      return 'Unknown time';
    }
  };

  return (
    <>
      <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <PageHeader
              breadcrumbs={[
                { label: 'Dashboard', href: '/agent/dashboard' },
                { label: 'Notifications' },
              ]}
              title="Notifications"
              subtitle="Stay updated with important alerts and updates"
              actions={
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600`}>
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-gray-600' : 'bg-gray-400'}`} />
                  {isConnected ? 'Live Updates' : 'Offline Mode'}
                </div>
              }
            />

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="mb-1 text-sm text-gray-600">Total Notifications</p>
                    <p className="text-2xl font-bold text-gray-900">{notifications.length}</p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <Bell className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="mb-1 text-sm text-gray-600">Unread</p>
                    <p className="text-2xl font-bold text-gray-900">{unreadCount}</p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <BellOff className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="mb-1 text-sm text-gray-600">Read</p>
                    <p className="text-2xl font-bold text-gray-900">{notifications.length - unreadCount}</p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="p-4 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                {/* Filter Buttons */}
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => setFilter("all")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === "all" 
                        ? "bg-gray-900 text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter("unread")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === "unread" 
                        ? "bg-gray-900 text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Unread ({unreadCount})
                  </button>
                  <button
                    onClick={() => setFilter("read")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === "read" 
                        ? "bg-gray-900 text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Read
                  </button>
                </div>

                {/* Type Filter */}
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                  >
                    <Filter className="w-4 h-4" />
                    Type
                  </button>
                  {showFilters && (
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    >
                      {notificationTypes.map(type => (
                        <option key={type.key} value={type.key}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 items-center">
                  <button
                    onClick={fetchNotifications}
                    className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg transition-colors hover:bg-gray-800"
                    >
                      <Check className="w-4 h-4" />
                      Mark All Read
                    </button>
                  )}
                  <button
                    onClick={() => {
                      // TODO: Implement notification settings modal
                      toast.info('Notification settings coming soon');
                    }}
                    className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="space-y-3 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              {loading ? (
                <div className="flex justify-center items-center p-12">
                  <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="p-12 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-gray-100 rounded-full">
                      <Bell className="w-8 h-8 text-gray-400" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">No notifications</h3>
                  <p className="text-sm text-gray-600">
                    {filter === "unread" 
                      ? "You're all caught up! No unread notifications." 
                      : filter === "read"
                      ? "No read notifications yet."
                      : "You don't have any notifications yet."}
                  </p>
                </div>
              ) : (
                filteredNotifications.map((notification, index) => (
                  <div
                    key={notification.notificationid}
                    onClick={() => handleNotificationClick(notification)}
                    className={`bg-white rounded-xl border border-gray-200 p-4 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-400 ${
                      !notification.is_read ? 'border-l-4 border-l-gray-900' : ''
                    }`}
                    style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                  >
                    <div className="flex gap-4 items-start">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          {getNotificationIcon(notification.type)}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-4 justify-between items-start">
                          <div className="flex-1">
                            <h3 className={`text-sm font-semibold mb-1 ${
                              !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </h3>
                            <p className="mb-2 text-sm text-gray-600">
                              {notification.message}
                            </p>
                            <div className="flex gap-3 items-center text-xs text-gray-500">
                              <div className="flex gap-1 items-center">
                                <Clock className="w-3 h-3" />
                                {formatTimeAgo(notification.createdat)}
                              </div>
                              {!notification.is_read && (
                                <div className="flex gap-1 items-center">
                                  <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                                  <span className="font-medium text-gray-900">New</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 items-center">
                            {!notification.is_read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.notificationid);
                                }}
                                className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDeleteNotification(e, notification.notificationid)}
                              className={`p-2 rounded-lg transition-colors ${
                                confirmDeleteId === notification.notificationid
                                  ? 'text-red-600 bg-red-50 hover:bg-red-100'
                                  : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                              }`}
                              title={confirmDeleteId === notification.notificationid ? 'Click again to confirm' : 'Delete'}
                            >
                              {confirmDeleteId === notification.notificationid ? (
                                <span className="text-[10px] font-medium">Delete?</span>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Empty State for No Filtered Results */}
            {!loading && notifications.length > 0 && filteredNotifications.length === 0 && (
              <div className="p-12 text-center bg-white rounded-xl border border-gray-200 shadow-sm animate-fade-in-up">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-gray-100 rounded-full">
                    <Filter className="w-8 h-8 text-gray-400" />
                  </div>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">No matching notifications</h3>
                <p className="text-sm text-gray-600">
                  Try adjusting your filters to see more notifications.
                </p>
              </div>
            )}
          </div>
    </>
  );
}